import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { endOfISOWeek, format } from 'date-fns';
import { BehaviorSubject } from 'rxjs';

import { removeTimeZoneOffset } from 'server/shared/helpers/removeTimeZoneOffset';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { EditDateRangeDialog } from '../date-range-dialog/date-range-dialog.component';

@Component({
  selector: 'app-date-range-select',
  templateUrl: './date-range-select.component.html',
  styleUrls: ['./date-range-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeSelectComponent implements OnInit {
  @Input() useQueryParam: string | false = false;

  chips$ = new BehaviorSubject<string[]>(['ALL', 'CUSTOM']);
  @Input() set chips(chips: string[]) {
    this.chips$.next(chips);
  }
  get chips() {
    return this.chips$.value;
  }

  @Input() set subscribeParams(value: BooleanInput) {
    this._subscribeParams = coerceBooleanProperty(value);
  }
  get subscribeParams(): boolean {
    return this._subscribeParams;
  }
  _subscribeParams = false;

  @Output() dateRangeChanged = new EventEmitter<string | undefined>();

  selectedRange$ = new BehaviorSubject<string>('ALL');
  @Input() set selectedRange(selectedRange: string) {
    this.selectedRange$.next(selectedRange);
  }
  get selectedRange() {
    return this.selectedRange$.value;
  }

  private _dialogFromDate?: Date;
  private _dialogToDate?: Date;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.subscribeParams) {
      this.route.queryParams.subscribe((p) => this.applyQueryParams(p));
    } else {
      this.applyQueryParams(this.route.snapshot.queryParams);
    }
  }

  openDialog(event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(EditDateRangeDialog, {
      data: {
        fromDate: this._dialogFromDate ? this._dialogFromDate : new Date(),
        toDate: this._dialogToDate
          ? this._dialogToDate
          : endOfISOWeek(new Date()),
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { fromDate: Date; toDate: Date }) => {
        if (result) {
          this.enableRange('CUSTOM', undefined, result);
          this._dialogFromDate = result.fromDate;
          this._dialogToDate = result.toDate;
        }
      });
  }

  enableRange(
    range: string,
    event?: Event,
    dateRange?: { fromDate: Date; toDate: Date },
  ) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedRange = range;
    let newQueryParam;
    if (dateRange) {
      newQueryParam = {
        range: [
          range,
          // the dates are used in UTC because job-date and availability-dates are stored in UTC
          format(removeTimeZoneOffset(dateRange.fromDate), dbDayDateToken),
          format(removeTimeZoneOffset(dateRange.toDate), dbDayDateToken),
        ].join(','),
      };
    } else {
      newQueryParam = { range: range };
    }
    if (this.selectedRange == 'ALL') {
      newQueryParam = { range: undefined };
    }
    this._updateQueryParams(newQueryParam);
  }

  applyQueryParams(queryParams: Params): void {
    if (this.useQueryParam && queryParams[this.useQueryParam] !== undefined) {
      let splitRange = queryParams[this.useQueryParam].split(',');
      this.selectedRange = splitRange[0];
      if (this.selectedRange === 'CUSTOM') {
        this._dialogFromDate = new Date(splitRange[1]);
        this._dialogToDate = new Date(splitRange[2]);
        this.dateRangeChanged.emit(
          [this.selectedRange, splitRange[1], splitRange[2]].join(','),
        );
      } else {
        this.dateRangeChanged.emit(this.selectedRange);
      }
    } else {
      this.selectedRange = 'ALL';
    }
  }

  private _updateQueryParams(queryParams: any) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.dateRangeChanged.emit(queryParams.range);
        });
    });
  }
}
